import {
  Component,
  OnInit,
  Inject,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';
import { UtilsService } from 'src/app/shared/utils.service';
import { RBACService } from 'src/app/shared/rbac.service';
import { AuditItemsSelectorComponent } from '../audit-items-selector/audit-items-selector.component';
import { AuditsModel } from '../audits.model';
import { AuditsService } from '../audits.service';

@Component({
  selector: 'app-audits-templates-edit',
  templateUrl: './audits-templates-edit.component.html',
  styleUrls: ['./audits-templates-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AuditsTemplatesEditComponent implements OnInit {
  /**
   * Is the form being submitted?
   */
  inProgress = false;

  // RBAC permission flags.
  canEdit: boolean = false;
  canCreate: boolean = false;
  canDelete: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public audit: AuditsModel,
    private utils: UtilsService,
    private dialog: MatDialog,
    private auditsService: AuditsService,
    private dialogRef: MatDialogRef<AuditsTemplatesEditComponent>,
    private rbac: RBACService
  ) {}

  ngOnInit() {
    // Determine if editing existing template or creating new.
    const isEdit = !isNaN(this.audit.id);
    const requiredPermission = isEdit
      ? RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_INSPECTIONS_AUDITS,
          RBACService.RBAC_A.A_EDIT
        )
      : RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_INSPECTIONS_AUDITS,
          RBACService.RBAC_A.A_CREATE
        );

    if (!this.rbac.userCanAccess([requiredPermission], true)) {
      const action = isEdit ? 'edit' : 'create';
      this.rbac.showGenericAccessDeniedMessage(
        `You do not have permission to ${action} inspection/audit templates.`
      );
      this.dialogRef.close();
      return;
    }

    // Resolve permission flags.
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_INSPECTIONS_AUDITS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canCreate = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_INSPECTIONS_AUDITS,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );
    this.canDelete =
      isEdit &&
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_TEMPLATES_INSPECTIONS_AUDITS,
            RBACService.RBAC_A.A_DELETE
          )
        ],
        true
      );

    if (isEdit) {
      this.find(this.audit.id);
    }
  }

  onSubmit() {
    if (this.inProgress) {
      return;
    }

    // Layer 2 guard: check correct permission based on create vs edit.
    const isEdit = !isNaN(this.audit.id);
    if (isEdit) {
      if (!this.canEdit) {
        this.rbac.showGenericAccessDeniedMessage(
          'You do not have permission to edit inspection/audit templates.'
        );
        return;
      }
    } else {
      if (!this.canCreate) {
        this.rbac.showGenericAccessDeniedMessage(
          'You do not have permission to create inspection/audit templates.'
        );
        return;
      }
    }

    this.inProgress = true;
    this.audit.template = true;
    isNaN(this.audit.id) ? this.create() : this.update();
  }

  onSelectAuditItems() {
    const dialogRef = this.dialog.open(AuditItemsSelectorComponent, {
      width: '1200px',
      data: { selected: this.audit.item_ids, multiple: true }
    });
    dialogRef.afterClosed().subscribe((itemIds: number[]) => {
      if (!Array.isArray(itemIds)) {
        return;
      }
      this.audit.item_ids = itemIds;
    });
  }

  delete(audits: AuditsModel) {
    // Layer 2 guard: check delete permission.
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete inspection/audit templates.'
      );
      return;
    }

    this.utils.showModal(
      'Remove Inspection/Audit template',
      `Are you sure you want to remove the "${
        audits.name
      }" Inspection/Audit template?`,
      () => {
        this.auditsService.remove([audits.id]).subscribe(() => {
          this.onSuccess(
            `The "${this.audit.name}" Inspection/Audit template was removed.`
          );
        });
      }
    );
  }

  private find(id: number) {
    this.auditsService.find(id).subscribe((result) => {
      this.audit = result.data;
      this.audit.item_ids = result.data.items.map((item) => item.id);
    });
  }

  private update() {
    this.auditsService.put(this.audit).subscribe({
      next: () =>
        this.onSuccess(
          `The "${this.audit.name}" Inspection/Audit template was updated.`
        ),
      error: () => (this.inProgress = false),
      complete: () => (this.inProgress = false)
    });
  }

  private create() {
    this.auditsService.post(this.audit).subscribe({
      next: () =>
        this.onSuccess(
          `The "${this.audit.name}" Inspection/Audit template was updated.`
        ),
      error: () => (this.inProgress = false),
      complete: () => (this.inProgress = false)
    });
  }

  private onSuccess(message: string) {
    this.utils.showToast(message);
    this.dialogRef.close(true);
  }
}
